import type { DocId, DocPerm, DocType, Edges, MapNode, NodeId, NodeMap, WorkflowState } from './types';
import { DOC_KINDS, WORKFLOW_DISPLAY, asWorkflowState, docTypeOf } from './docs';
import { OWED_CUSTOM_LABEL, OWED_PILL_LABEL, plannedMissing } from './health';
import { effectiveResponsibles } from './responsibility';

/**
 * The dashboard's DOCUMENTS TABLE, as data (V0.44) — pure and DOM-free, like `lib/stats.ts`.
 *
 * ONE ROW PER DOCUMENT PAGE, plus one row per document a node's plan OWES and nobody has
 * created. The rows are a JOIN of two payloads the client already holds for the same caller:
 * `GET /api/docs/dashboard` (per-language state, staleness, responsibles, the AI facts) and
 * `GET /api/map` (titles, doc types, node labels, the plan, the AI-generated flag). Nothing here
 * re-derives a rule that lives elsewhere:
 *
 *  - PLANNED rows come from `plannedMissing()` (lib/health.ts) — "the ONE bridge from plan keys
 *    to attachment" — so the table, the map rows, the node panel and the doc band cannot
 *    disagree about what a node still owes. The server emits none of these.
 *  - `canDocBtn` is applied here, as the UX layer it is everywhere else (`visibleDocsOf`): a
 *    kind whose button is off for this viewer is neither drawn nor counted. The server scopes by
 *    VISIBILITY and never by doc buttons — the boundary is theirs, the preference is ours.
 *  - Node responsibles are `effectiveResponsibles()` (lib/responsibility.ts), the client half of
 *    the resolver. Doc responsibles arrive group-expanded, so "mine" is `includes(me)`.
 *
 * THE CURRENT-LANGUAGE READING mirrors `/workflows`' requested → source fallback for DISPLAY
 * only: a page not written in the current language shows as `missing`, with the source-locale
 * cell beside it so the UI can say "not written in FR — you would be reading EN (Approved)".
 * That is a payload-shape contract, not a permission rule; the gates (`canEditDoc`,
 * `canAssignDoc`, `canEditPrompt`) keep reading `S.docWorkflow`, the server-resolved entry.
 *
 * "NO DATA YET" renders as the id: a name the directory cannot vouch for is shown raw rather
 * than invented, the same rule `lib/responsibility.ts` states for its predicates.
 */

/* ------------------------------------------------------------------- the wire shape ---- */

/*
 * What `GET /api/docs/dashboard` (V0.44) returns. Declared HERE, in the pure core, rather than
 * in `api/docs.ts` (which re-exports them): the e2e suite imports this library to compute its
 * expectations, and `api/` reaches Vite-typed config the suite's compiler does not know.
 *
 * The payload is LOCALE-COMPLETE — every language has a cell — and deliberately lacks what the
 * map payload already carries for the same caller (titles, slugs, types, the AI-generated flag,
 * the plan, node labels). The `ai` blocks and `aiByNode` are present only for an AI_ADMIN; the
 * server omits them otherwise, and that absence is the client's gate too.
 */

export type DashboardChangeKind = 'human' | 'ai';

export interface DashboardLocaleAi {
  /** Highest revision number in this language, or null when nothing was ever written. */
  version: number | null;
  /** The newest successful AI write in this language (generation or translation). */
  lastRun: { at: string; source: string; status: string; model: string | null } | null;
  /** Whether the chatbot's index holds this language's text. */
  embedded: boolean;
}

export interface DashboardLocaleCell {
  /** A translation row exists. False = nothing written yet — the cell still carries responsibles. */
  written: boolean;
  /** One of the five workflow states, or null when `written` is false. */
  workflowState: string | null;
  /** When and by whom the workflow state was last moved BY HAND — a name, never "approved by". */
  reviewedAt: string | null;
  reviewedByName: string | null;
  /** The staleness rule (server/src/docs/revisions.ts): never written counts as fully behind. */
  stale: boolean;
  behind: number;
  /** The newest revision that actually changed the page — the feed's reading of "last change". */
  lastChangeAt: string | null;
  lastChangeBy: string | null;
  lastChangeKind: DashboardChangeKind | null;
  /** ELIGIBLE doc responsibles for this language — groups already expanded to their members. */
  responsibles: string[];
  /** The groups directly assigned — display-only ids, members folded into `responsibles`. */
  responsibleGroups: string[];
  /** AI_ADMIN only. */
  ai?: DashboardLocaleAi;
}

export interface DashboardDoc {
  docPageId: string;
  /** `DocPage.kind` — the one fact the map does not carry about a page. */
  kind: string;
  /** The VISIBLE nodes carrying this page in any slot. Empty = an unfiled page. */
  nodeIds: string[];
  locales: Record<string, DashboardLocaleCell>;
}

export interface DashboardNodeAi {
  /** Human edits since the last generation of this node's primary page — the regeneration guard. */
  guard: { count: number; lastAt: string; lastBy: string } | null;
  /** The newest live-verification run against this node. */
  lastLiveRun: { at: string; state: string; finishedAt: string | null } | null;
}

export interface DocDashboardPayload {
  sourceLocale: string;
  languages: { code: string; name: string }[];
  docs: DashboardDoc[];
  /** AI_ADMIN only — absent for everyone else, which is what the client gates on. */
  aiByNode?: Record<string, DashboardNodeAi>;
}

/**
 * The languages in DISPLAY order: the source first, then the rest by code — EN, DE, FR on this
 * installation (V0.44.1). The server sends them by code alone; every language strip in the
 * dashboard (the chips, the popover, the AI console's cell) reads this instead, so the source is
 * always the first pill and the two tabs agree on the order.
 */
export function orderedLanguages<T extends { code: string }>(languages: readonly T[], sourceLocale: string): T[] {
  return [...languages].sort((a, b) => {
    if (a.code === sourceLocale) return -1;
    if (b.code === sourceLocale) return 1;
    return a.code < b.code ? -1 : a.code > b.code ? 1 : 0;
  });
}

/* ------------------------------------------------------------------------- the rows ---- */

export type DocRowState = WorkflowState | 'missing' | 'planned';

export interface DocOwner {
  nodeId: NodeId;
  label: string;
  /** Ancestor labels, root first, the node itself excluded. */
  breadcrumb: string[];
}

/** The AI operator's facts for one row — present only when the payload carried `aiByNode`. */
export interface DocRowAi {
  generated: boolean;
  /** The owner whose PRIMARY slot is this page — the node the guard and live runs belong to. */
  primaryNodeId: NodeId | null;
  guard: DashboardNodeAi['guard'];
  lastLiveRun: DashboardNodeAi['lastLiveRun'];
  liveObservationAt: string | null;
}

interface RowCommon {
  key: string;
  docKind: DocPerm;
  kindLabel: string;
  title: string;
  owners: DocOwner[];
  /** Names the search box matches and the Responsible column can show. */
  responsibleNames: string[];
  anyStale: boolean;
  lastChangeAt: string | null;
}

export interface DocRowDoc extends RowCommon {
  kind: 'doc';
  docPageId: DocId;
  docType: DocType;
  state: WorkflowState | 'missing';
  /** The current language's cell (null only when that language is not in the payload). */
  current: DashboardLocaleCell | null;
  /** The source-locale cell, set only when the current language is unwritten and the source is. */
  fallback: DashboardLocaleCell | null;
  byLocale: Record<string, DashboardLocaleCell>;
  responsibles: { docUsers: string[]; docGroups: string[]; nodeUsers: string[] };
  ai?: DocRowAi;
}

export interface DocRowPlanned extends RowCommon {
  kind: 'planned';
  nodeId: NodeId;
  state: 'planned';
  /** For a custom row: which of the still-owed custom documents this is (0-based). */
  customIndex?: number;
  responsibles: { nodeUsers: string[] };
}

export type DocRow = DocRowDoc | DocRowPlanned;

export interface DocTableInput {
  nodes: NodeMap;
  edges: Edges;
  docTitles: Record<string, string>;
  docTypes: Record<string, DocType>;
  docAiGenerated: Record<string, boolean>;
  payload: DocDashboardPayload;
  canDocBtn: (perm: DocPerm) => boolean;
  isPermHidden: (id: NodeId) => boolean;
  userDirectory: readonly { id: string; displayName: string }[];
  groupDirectory: readonly { id: string; name: string }[];
  currentLocale: string;
}

const DOC_PERMS: readonly DocPerm[] = ['documentation', 'internal', 'course', 'demo', 'custom'];
const ANCESTOR_GUARD = 10_000; // the map is user-editable; a cycle must not hang the client

const asDocPerm = (kind: string): DocPerm => (DOC_PERMS as readonly string[]).includes(kind) ? (kind as DocPerm) : 'custom';

export const kindLabelOf = (perm: DocPerm): string =>
  DOC_KINDS.find((k) => k.perm === perm)?.label ?? OWED_CUSTOM_LABEL;

/** Ancestor labels root → parent, the node itself excluded. */
export function breadcrumbOf(nodeId: NodeId, nodes: NodeMap, edges: Edges): string[] {
  const out: string[] = [];
  let cur = edges[nodeId];
  let guard = 0;
  while (cur !== undefined && guard++ < ANCESTOR_GUARD) {
    out.unshift(nodes[cur]?.label ?? '');
    cur = edges[cur];
  }
  return out;
}

function ownerOf(nodeId: NodeId, nodes: NodeMap, edges: Edges): DocOwner {
  return { nodeId, label: nodes[nodeId]?.label ?? '', breadcrumb: breadcrumbOf(nodeId, nodes, edges) };
}

export function buildDocRows(input: DocTableInput): DocRow[] {
  const { nodes, edges, payload } = input;
  const userName = new Map(input.userDirectory.map((u) => [u.id, u.displayName]));
  const groupName = new Map(input.groupDirectory.map((g) => [g.id, g.name]));
  const nameOf = (id: string) => userName.get(id) ?? id;
  const groupNameOf = (id: string) => groupName.get(id) ?? id;
  const aiVisible = payload.aiByNode !== undefined;
  const rows: DocRow[] = [];

  for (const d of payload.docs) {
    const docKind = asDocPerm(d.kind);
    if (!input.canDocBtn(docKind)) continue;
    const visibleOwnerIds = d.nodeIds.filter((id) => nodes[id] && !input.isPermHidden(id));
    // Carried by nodes this client hides, and by nothing else: not ours to show as unfiled.
    if (d.nodeIds.length && !visibleOwnerIds.length) continue;
    const owners = visibleOwnerIds.map((id) => ownerOf(id, nodes, edges));

    const current = d.locales[input.currentLocale] ?? null;
    const source = d.locales[payload.sourceLocale] ?? null;
    const fallback = current && !current.written && source?.written && source !== current ? source : null;
    const state: WorkflowState | 'missing' = current?.written ? asWorkflowState(current.workflowState) : 'missing';

    const docUsers = current?.responsibles ?? [];
    const docGroups = current?.responsibleGroups ?? [];
    const nodeUsers = [...new Set(owners.flatMap((o) => effectiveResponsibles(o.nodeId, nodes, edges)))];
    const responsibleNames =
      docUsers.length || docGroups.length
        ? [...docUsers.map(nameOf), ...docGroups.map(groupNameOf)]
        : nodeUsers.map(nameOf);

    let ai: DocRowAi | undefined;
    if (aiVisible) {
      const primaryNodeId = owners.find((o) => nodes[o.nodeId]?.docId === d.docPageId)?.nodeId ?? null;
      const nodeAi = primaryNodeId ? payload.aiByNode?.[primaryNodeId] : undefined;
      ai = {
        generated: !!input.docAiGenerated[d.docPageId],
        primaryNodeId,
        guard: nodeAi?.guard ?? null,
        lastLiveRun: nodeAi?.lastLiveRun ?? null,
        liveObservationAt: primaryNodeId ? (nodes[primaryNodeId]?.liveObservationAt ?? null) : null,
      };
    }

    rows.push({
      kind: 'doc',
      key: d.docPageId,
      docPageId: d.docPageId,
      docKind,
      kindLabel: kindLabelOf(docKind),
      docType: docTypeOf(input.docTypes, d.docPageId),
      title: input.docTitles[d.docPageId] ?? '',
      owners,
      state,
      current,
      fallback,
      byLocale: d.locales,
      responsibles: { docUsers, docGroups, nodeUsers },
      responsibleNames,
      anyStale: Object.values(d.locales).some((c) => c.stale),
      lastChangeAt: current?.lastChangeAt ?? null,
      ...(ai ? { ai } : {}),
    });
  }

  // What each visible node still OWES — the one bridge from plan to pills, reused verbatim.
  for (const nodeId of Object.keys(nodes)) {
    const n: MapNode = nodes[nodeId];
    if (n.root || input.isPermHidden(nodeId)) continue;
    const owed = plannedMissing(n, input.canDocBtn);
    if (!owed.kinds.length && !owed.customMissing) continue;
    const owner = ownerOf(nodeId, nodes, edges);
    const nodeUsers = effectiveResponsibles(nodeId, nodes, edges);
    const base = {
      kind: 'planned' as const,
      nodeId,
      owners: [owner],
      state: 'planned' as const,
      responsibles: { nodeUsers },
      responsibleNames: nodeUsers.map(nameOf),
      anyStale: false,
      lastChangeAt: null,
    };
    for (const k of owed.kinds) {
      rows.push({ ...base, key: `planned:${nodeId}:${k}`, docKind: k, kindLabel: kindLabelOf(k), title: OWED_PILL_LABEL(kindLabelOf(k)) });
    }
    for (let i = 0; i < owed.customMissing; i++) {
      rows.push({
        ...base,
        key: `planned:${nodeId}:custom:${i}`,
        docKind: 'custom',
        kindLabel: OWED_CUSTOM_LABEL,
        title: OWED_PILL_LABEL(OWED_CUSTOM_LABEL),
        customIndex: i,
      });
    }
  }

  return rows;
}

/* ---------------------------------------------------------------------- filtering ------ */

export interface DocTableFilters {
  /** Matched, lower-cased, against title, kind, node labels, breadcrumbs and responsible names. */
  text: string;
  kind: DocPerm | 'all';
  /** Applies to doc rows only; a planned row has no workflow state and is excluded while set. */
  state: WorkflowState | 'missing' | 'all';
  planned: 'include' | 'only' | 'exclude';
  /** Any language behind the source. */
  stale: boolean;
  /** 'me' and a user id match the page's own assignees, or — when it has none — the node's. */
  responsible: 'all' | 'me' | 'unassigned' | string;
  /** Restrict to one node's subtree (the node itself included). */
  node: NodeId | null;
  /** Restrict to rows this EXACT node owns — the cross-tab hand-off's "this node's documents",
   *  which is not its subtree (V0.44.1). */
  owner: NodeId | null;
  /* AI operator only — ignored for everyone else because no row carries `ai`. */
  aiGenerated: boolean;
  guarded: boolean;
  noEmbeddings: boolean;
}

export const DEFAULT_FILTERS: DocTableFilters = Object.freeze({
  text: '',
  kind: 'all',
  state: 'all',
  planned: 'include',
  stale: false,
  responsible: 'all',
  node: null,
  owner: null,
  aiGenerated: false,
  guarded: false,
  noEmbeddings: false,
});

/** Is `nodeId` at or below `root` in the (inverted, child → parent) edge map? */
export function inSubtree(nodeId: NodeId, root: NodeId, edges: Edges): boolean {
  let cur: NodeId | undefined = nodeId;
  let guard = 0;
  while (cur !== undefined && guard++ < ANCESTOR_GUARD) {
    if (cur === root) return true;
    cur = edges[cur];
  }
  return false;
}

export function filterRows(
  rows: readonly DocRow[],
  f: DocTableFilters,
  ctx: { edges: Edges; currentUserId: string | null },
): DocRow[] {
  const q = f.text.trim().toLowerCase();
  return rows.filter((r) => {
    if (f.planned === 'only' && r.kind !== 'planned') return false;
    if (f.planned === 'exclude' && r.kind === 'planned') return false;
    if (f.kind !== 'all' && r.docKind !== f.kind) return false;
    if (f.state !== 'all' && (r.kind !== 'doc' || r.state !== f.state)) return false;
    if (f.stale && !r.anyStale) return false;
    if (f.node && !r.owners.some((o) => inSubtree(o.nodeId, f.node!, ctx.edges))) return false;
    if (f.owner && !r.owners.some((o) => o.nodeId === f.owner)) return false;

    if (f.responsible !== 'all') {
      const direct = r.kind === 'doc' ? r.responsibles.docUsers : [];
      const directGroups = r.kind === 'doc' ? r.responsibles.docGroups : [];
      const inherited = r.responsibles.nodeUsers;
      if (f.responsible === 'unassigned') {
        if (r.kind === 'doc' ? direct.length || directGroups.length : inherited.length) return false;
      } else {
        const who = f.responsible === 'me' ? ctx.currentUserId : f.responsible;
        if (!who) return false;
        const hit = direct.length || directGroups.length ? direct.includes(who) : inherited.includes(who);
        if (!hit) return false;
      }
    }

    if (r.kind === 'doc' && r.ai) {
      if (f.aiGenerated && !r.ai.generated) return false;
      if (f.guarded && !r.ai.guard) return false;
      if (f.noEmbeddings && !Object.values(r.byLocale).some((c) => c.written && c.ai && !c.ai.embedded)) return false;
    } else if (f.aiGenerated || f.guarded || f.noEmbeddings) {
      return false; // an AI-only filter over rows without AI facts matches nothing, honestly
    }

    if (q) {
      const hay = [
        r.title,
        r.kindLabel,
        ...r.owners.flatMap((o) => [o.label, ...o.breadcrumb]),
        ...r.responsibleNames,
      ]
        .join(' ')
        .toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

/* ------------------------------------------------------------------------ sorting ------ */

export type DocSortKey = 'title' | 'node' | 'kind' | 'state' | 'responsible' | 'lastChange' | 'stale';

/** Lifecycle order: what is owed, then what is unwritten, then the five workflow states. */
export const STATE_ORDER: readonly DocRowState[] = [
  'planned',
  'missing',
  ...WORKFLOW_DISPLAY.map((s) => s.key as WorkflowState),
];

const plainTitle = (r: DocRow) => r.title.replace(/^\+\s*/, '').toLowerCase();

function sortValue(r: DocRow, key: DocSortKey): string | number {
  switch (key) {
    case 'title':
      return plainTitle(r);
    case 'node':
      return r.owners.length ? [...r.owners[0].breadcrumb, r.owners[0].label].join(' › ').toLowerCase() : '￿';
    case 'kind':
      return DOC_PERMS.indexOf(r.docKind);
    case 'state':
      return STATE_ORDER.indexOf(r.state);
    case 'responsible':
      return r.responsibleNames.length ? r.responsibleNames[0].toLowerCase() : '￿';
    case 'lastChange':
      return r.lastChangeAt ?? '';
    case 'stale':
      return r.anyStale ? 0 : 1;
  }
}

/** A stable sort: equal keys fall back to the title, so a re-sort never shuffles rows. */
export function sortRows(rows: readonly DocRow[], key: DocSortKey, asc: boolean): DocRow[] {
  const dir = asc ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = sortValue(a, key);
    const bv = sortValue(b, key);
    if (av < bv) return -dir;
    if (av > bv) return dir;
    const at = plainTitle(a);
    const bt = plainTitle(b);
    return at < bt ? -1 : at > bt ? 1 : 0;
  });
}

/* ---------------------------------------------------------------------- the strip ------ */

export interface DocTableSummary {
  total: number;
  byState: Record<DocRowState, number>;
  /** Doc rows with at least one language behind the source. */
  stale: number;
}

/** The counts the strip above the table shows — from the SAME rows, so the two cannot disagree. */
export function summarizeRows(rows: readonly DocRow[]): DocTableSummary {
  const byState = Object.fromEntries(STATE_ORDER.map((s) => [s, 0])) as Record<DocRowState, number>;
  let stale = 0;
  for (const r of rows) {
    byState[r.state] += 1;
    if (r.anyStale) stale += 1;
  }
  return { total: rows.length, byState, stale };
}
